import React, { useState, useRef } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { Mail, Phone, MapPin, Send, MessageCircle, CheckCircle, Sparkles } from 'lucide-react';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Button } from './ui/button';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [errors, setErrors] = useState({});
  
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        newErrors.email = 'Please enter a valid email';
      }
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);

    // Show success animation first
    setShowSuccess(true);

    // Show toast
    toast.success('Opening WhatsApp...', {
      description: "Your message is ready to send!",
      duration: 3000,
    });

    // Create WhatsApp message
    const phoneNumber = '919399497650'; // Your WhatsApp number
    const whatsappMessage = encodeURIComponent(
      `📩 *New Inquiry from Website*\n\n` +
      `👤 *Name:* ${formData.name}\n` +
      `📧 *Email:* ${formData.email}\n` +
      `${formData.phone ? `📱 *Phone:* ${formData.phone}\n` : ''}` +
      `\n💬 *Message:*\n${formData.message}`
    );

    // Open WhatsApp after a brief delay to show the success animation
    setTimeout(() => {
      // Open WhatsApp with pre-filled message
      const whatsappURL = `https://wa.me/${phoneNumber}?text=${whatsappMessage}`;
      window.open(whatsappURL, '_blank');

      // Reset form
      setFormData({ name: '', email: '', phone: '', message: '' });
      setShowSuccess(false);
      setIsSubmitting(false);
    }, 1200);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: 'Email',
      value: 'yuvidhakad396@gmail.com',
      link: 'mailto:yuvidhakad396@gmail.com',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      icon: Phone,
      title: 'Phone',
      value: '+91 9399497650',
      link: 'tel:+919399497650',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: MapPin,
      title: 'Location',
      value: 'Indore, India',
      link: null,
      color: 'from-cyan-500 to-blue-500'
    }
  ];

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.12, delayChildren: 0.2 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }
    }
  };

  const formFieldVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { 
        duration: 0.5, 
        delay: i * 0.1,
        ease: [0.25, 0.46, 0.45, 0.94] 
      }
    })
  };

  const formVariants = {
    hidden: { opacity: 0, x: 50 },
    visible: { 
      opacity: 1, 
      x: 0,
      transition: { duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94], delay: 0.2 }
    }
  };

  return (
    <section 
      id="contact" 
      ref={sectionRef}
      className="relative py-24 overflow-hidden bg-gradient-to-b from-white via-slate-50/30 to-white"
      data-testid="contact-section"
    >
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-1/4 w-80 h-80 bg-blue-100/30 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-1/4 w-80 h-80 bg-purple-100/30 rounded-full blur-3xl" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div 
          className="text-center mb-16"
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
        >
          <motion.div
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-100 mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Sparkles className="w-4 h-4 text-blue-600" />
            <span className="text-sm font-medium text-slate-700">Get In Touch</span>
          </motion.div>
          
          <h2 
            className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 bg-gradient-to-r from-slate-900 via-blue-800 to-purple-800 bg-clip-text text-transparent"
            data-testid="contact-heading"
          >
            Let's Talk
          </h2>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed">
            Ready to bring your vision to life? We'd love to hear from you. 
            Drop us a message and let's start building something amazing together.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-start">
          {/* Left Side - Contact Info */}
          <motion.div 
            className="space-y-5"
            variants={containerVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
          >
            {/* Contact Info Cards */}
            {contactInfo.map((info, index) => {
              const IconComponent = info.icon;
              return (
                <motion.div
                  key={index}
                  variants={cardVariants}
                  whileHover={{ 
                    y: -4, 
                    boxShadow: "0 20px 40px -12px rgba(0, 0, 0, 0.12)"
                  }}
                  transition={{ duration: 0.3 }}
                  data-testid={`contact-card-${info.title.toLowerCase()}`}
                >
                  {info.link ? (
                    <a href={info.link} className="block">
                      <ContactCard info={info} IconComponent={IconComponent} />
                    </a>
                  ) : (
                    <ContactCard info={info} IconComponent={IconComponent} />
                  )}
                </motion.div>
              );
            })}

            {/* WhatsApp Direct CTA */}
            <motion.div
              variants={cardVariants}
              whileHover={{ 
                y: -4, 
                boxShadow: "0 20px 40px -12px rgba(34, 197, 94, 0.2)"
              }}
              className="relative group cursor-pointer"
              onClick={() => window.open('https://wa.me/919399497650', '_blank')}
              data-testid="whatsapp-cta-card"
            >
              <div className="relative bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-5 border border-green-200/60 overflow-hidden">
                <motion.div 
                  className="absolute inset-0 bg-gradient-to-r from-green-400/5 to-emerald-400/5"
                  animate={{ 
                    scale: [1, 1.02, 1],
                    opacity: [0.5, 0.8, 0.5]
                  }}
                  transition={{ 
                    duration: 2.5, 
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                />
                
                <div className="relative flex items-center gap-4">
                  <motion.div 
                    className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg shadow-green-500/25"
                    animate={{ scale: [1, 1.03, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
                  >
                    <MessageCircle className="w-6 h-6 text-white" />
                  </motion.div>
                  <div className="flex-1">
                    <p className="text-sm text-green-700/80 font-medium">
                      Prefer instant messaging?
                    </p>
                    <p className="text-lg font-bold text-green-800 group-hover:text-green-900 transition-colors">
                      Chat on WhatsApp
                    </p>
                  </div>
                  <Send className="w-5 h-5 text-green-600 group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Right Side - Contact Form */}
          <motion.div
            variants={formVariants}
            initial="hidden"
            animate={isInView ? "visible" : "hidden"}
            className="relative"
          >
            <div 
              className="relative bg-white rounded-3xl p-8 lg:p-10 shadow-xl shadow-slate-200/60 border border-slate-100/80"
              data-testid="contact-form-container"
            >
              {/* Success Overlay */}
              <AnimatePresence>
                {showSuccess && (
                  <motion.div
                    className="absolute inset-0 bg-white/95 backdrop-blur-sm rounded-3xl z-10 flex flex-col items-center justify-center"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <motion.div
                      className="w-20 h-20 bg-gradient-to-br from-green-400 to-emerald-500 rounded-full flex items-center justify-center shadow-lg shadow-green-500/30 mb-6"
                      initial={{ scale: 0, rotate: -180 }}
                      animate={{ scale: 1, rotate: 0 }}
                      transition={{ type: "spring", stiffness: 200, damping: 15 }}
                    >
                      <MessageCircle className="w-10 h-10 text-white" />
                    </motion.div>
                    <motion.h3
                      className="text-2xl font-bold text-slate-900 mb-2"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.2 }}
                    >
                      Opening WhatsApp...
                    </motion.h3>
                    <motion.p
                      className="text-slate-600 text-center max-w-xs"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.3 }}
                    >
                      Your message is ready to send!
                    </motion.p>
                    <motion.div
                      className="mt-4 flex items-center gap-2 text-green-600"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 0.4 }}
                    >
                      <motion.div 
                        className="w-5 h-5 border-2 border-green-300 border-t-green-600 rounded-full"
                        animate={{ rotate: 360 }}
                        transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      />
                      <span className="text-sm font-medium">Redirecting...</span>
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="relative">
                <h3 className="text-2xl font-bold text-slate-900 mb-2">
                  Send us a Message
                </h3>
                <p className="text-slate-500 mb-8">
                  Fill out the form below and we'll get back to you shortly
                </p>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="contact-form">
                  {/* Name Field */}
                  <motion.div
                    custom={0}
                    variants={formFieldVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <label 
                      htmlFor="name" 
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Name <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="Your full name"
                        value={formData.name}
                        onChange={handleChange}
                        disabled={isSubmitting || showSuccess}
                        data-testid="input-name"
                        className={`w-full h-12 px-4 rounded-xl border-2 transition-all duration-300 bg-slate-50/50 
                          focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
                          group-hover:border-slate-300
                          ${errors.name ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : 'border-slate-200'}`}
                      />
                      {errors.name && (
                        <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 text-sm mt-1.5"
                        >
                          {errors.name}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>

                  {/* Email Field */}
                  <motion.div
                    custom={1}
                    variants={formFieldVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <label 
                      htmlFor="email" 
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="your.email@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        disabled={isSubmitting || showSuccess}
                        data-testid="input-email"
                        className={`w-full h-12 px-4 rounded-xl border-2 transition-all duration-300 bg-slate-50/50 
                          focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
                          group-hover:border-slate-300
                          ${errors.email ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : 'border-slate-200'}`}
                      />
                      {errors.email && (
                        <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 text-sm mt-1.5"
                        >
                          {errors.email}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>

                  {/* Phone Field */}
                  <motion.div
                    custom={2}
                    variants={formFieldVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <label 
                      htmlFor="phone" 
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Phone <span className="text-slate-400 text-xs font-normal">(Optional)</span>
                    </label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="+91 98765 43210"
                      value={formData.phone}
                      onChange={handleChange}
                      disabled={isSubmitting || showSuccess}
                      data-testid="input-phone"
                      className="w-full h-12 px-4 rounded-xl border-2 border-slate-200 hover:border-slate-300 transition-all duration-300 bg-slate-50/50 focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
                    />
                  </motion.div>

                  {/* Message Field */}
                  <motion.div
                    custom={3}
                    variants={formFieldVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <label 
                      htmlFor="message" 
                      className="block text-sm font-semibold text-slate-700 mb-2"
                    >
                      Message <span className="text-red-500">*</span>
                    </label>
                    <div className="relative group">
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us about your project..."
                        value={formData.message}
                        onChange={handleChange}
                        disabled={isSubmitting || showSuccess}
                        rows={4}
                        data-testid="input-message"
                        className={`w-full px-4 py-3 rounded-xl border-2 transition-all duration-300 bg-slate-50/50 resize-none
                          focus:bg-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10
                          group-hover:border-slate-300
                          ${errors.message ? 'border-red-400 focus:border-red-500 focus:ring-red-500/10' : 'border-slate-200'}`}
                      />
                      {errors.message && (
                        <motion.p 
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="text-red-500 text-sm mt-1.5"
                        >
                          {errors.message}
                        </motion.p>
                      )}
                    </div>
                  </motion.div>

                  {/* Submit Button */}
                  <motion.div
                    custom={4}
                    variants={formFieldVariants}
                    initial="hidden"
                    animate={isInView ? "visible" : "hidden"}
                  >
                    <motion.div
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <Button
                        type="submit"
                        disabled={isSubmitting || showSuccess}
                        data-testid="submit-button"
                        className="relative w-full h-14 text-base font-semibold rounded-xl bg-gradient-to-r from-green-500 via-green-600 to-emerald-600 hover:from-green-600 hover:via-green-700 hover:to-emerald-700 text-white shadow-lg shadow-green-500/25 hover:shadow-xl hover:shadow-green-500/30 transition-all duration-300 overflow-hidden group disabled:opacity-70"
                      >
                        {/* Shimmer effect */}
                        <span className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" />
                        
                        <span className="relative flex items-center justify-center gap-2">
                          {isSubmitting ? (
                            <>
                              <motion.div 
                                className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                                animate={{ rotate: 360 }}
                                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                              />
                              Sending to WhatsApp...
                            </>
                          ) : (
                            <>
                              <MessageCircle className="w-5 h-5" />
                              Send via WhatsApp
                            </>
                          )}
                        </span>
                      </Button>
                    </motion.div>
                    
                    {/* Helper text */}
                    <p className="text-xs text-slate-400 text-center mt-3">
                      Your message will open in WhatsApp for instant delivery
                    </p>
                  </motion.div>
                </form>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Reusable Contact Card Component
const ContactCard = ({ info, IconComponent }) => (
  <div className="relative bg-white rounded-2xl p-5 border border-slate-100 shadow-lg shadow-slate-100/50 overflow-hidden group cursor-pointer">
    <div className={`absolute inset-0 bg-gradient-to-r ${info.color} opacity-0 group-hover:opacity-5 transition-opacity duration-300`} />
    
    <div className="relative flex items-center gap-4">
      <div className={`w-12 h-12 bg-gradient-to-br ${info.color} rounded-xl flex items-center justify-center shadow-lg`}>
        <IconComponent className="w-5 h-5 text-white" />
      </div>
      <div>
        <p className="text-sm text-slate-500 font-medium mb-0.5">{info.title}</p>
        <p className="text-base font-semibold text-slate-900 group-hover:text-blue-700 transition-colors">
          {info.value}
        </p>
      </div>
    </div>
  </div>
);

export default Contact;
